<template>
  <b-container
    class="cooling-efficiency-chart"
    fluid
  >
    <div class="cooling-efficiency-chart__wrapper">
      <efficiency-dropdown
        :tab-info="tabInfo"
        :selected-norm="selectedNorm"
        :emit-event="false"
        @change-norm="onChangeNorm"
      />
      <default-chart
        chart-id="cooling-efficiency-chart"
        x-data-manager="dateTime"
        :chart-data="chartData"
        :loading="loading"
        :remove-outlier.sync="removeChartOutlier"
        :enable-multi-axis.sync="enableChartMultiAxis"
        :colors-list="chartColors"
        @toggle-outlier="getChartData(true)"
        @toggle-multi-axis="getChartData(true)"
      />
    </div>
  </b-container>
</template>

<script>
import DefaultChart from '@/components/charts/DefaultChart';
import defaultChartMixin from '@/domain/mixins/defaultChartMixin';
import eventBus from '@/services/globalEventBus';
import Service from '@/domain/services/installation/engineering/coolingEfficiency/coolingEfficiencyService';
import coolingEfficiencyEvents from '@/domain/events/coolingEfficiency';
import themeService from '@/services/themeService';
import engineeringEvents from '@/domain/events/engineering';
import { chartColors } from '@/domain/data/charts/chartColors';
import coolingEfficiencyGraphicIds from '@/domain/data/installation/engineering/coolingEfficiency/coolingEfficiencyGraphicIds';
import EfficiencyDropdown from '@/domain/components/installation/engineering/coolingEfficiency/EfficiencyDropdown';

export default {
  name: 'CoolingEfficiencyChartTab',
  props: {
    tabInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    DefaultChart,
    EfficiencyDropdown,
  },
  mixins: [defaultChartMixin],
  data: vm => ({
    chartData: [],
    loading: false,
    selectedNorm: vm.tabInfo.selectedNorm,
  }),
  computed: {
    chartColors() {
      return [
        themeService.getStyleVar('color-warning'),
        `${themeService.getStyleVar('color-danger')}8c`,
        themeService.getStyleVar('color-warning'),
        `${themeService.getStyleVar('color-danger')}8c`,
        themeService.getStyleVar('chart-color-blue'),
        themeService.getPrimaryColor(),
        ...chartColors,
      ];
    },
  },
  created() {
    this.init();
    this.refreshData(true);
  },
  beforeDestroy() {
    this.deinit();
  },
  methods: {
    init() {
      eventBus.on(engineeringEvents.refreshDataTab, this.refreshData, this);
      eventBus.on(coolingEfficiencyEvents.refreshSelectedNorm, this.refreshData, this);
    },
    deinit() {
      eventBus.off(engineeringEvents.refreshDataTab, this.refreshData, this);
      eventBus.off(coolingEfficiencyEvents.refreshSelectedNorm, this.refreshData, this);
    },
    refreshData(showLoading = false) {
      if (this.tabInfo.status?.lastUpdate) {
        this.getChartData(showLoading);
      }
    },
    getChartData(showLoading = false) {
      this.loading = showLoading;
      const queryString = {
        ...this.chartFilters,
        normId: this.selectedNorm,
      };

      Service.getChart(this.$route.params.assetModuleId, queryString).then((response) => {
        this.chartData = this.sortChartDataByType(response);
      }).finally(() => {
        this.loading = false;
      });
    },
    sortChartDataByType(chartData) {
      const ids = coolingEfficiencyGraphicIds[this.selectedNorm] || [];
      const orderList = Object.values(ids);
      const orderedItems = chartData.filter(item => orderList.includes(item.id));
      const unorderedItems = chartData.filter(item => !orderList.includes(item.id));

      orderedItems.sort((a, b) => orderList.indexOf(a.id) - orderList.indexOf(b.id));

      return [
        ...orderedItems,
        ...unorderedItems,
      ];
    },
    onChangeNorm(event) {
      this.selectedNorm = event;
      this.refreshData(true);
    },
  },
};

</script>

<style lang="sass" scoped>
.cooling-efficiency-chart
  &__filters
    display: flex
    flex-direction: column
    margin-bottom: 15px

  &__norms ::v-deep
    .dropdown-item
      .alert-icon
        color: var(--color-danger-light)
        font-size: 13px
      .diagnostic-icon
        color: var(--color-warning)
        font-size: 13px
    .dropdown-item:hover
      .fas
        color: white
      svg path
        fill: white
</style>
